import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { InternalNavbar } from "@/components/InternalNavbar"
import { useLanguage } from "@/hooks/use-language"
import { useAuth } from "@/contexts/AuthContext"
import { useLicense } from "@/hooks/use-license"
import { toast } from "sonner"
import {
  Crown,
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Lock,
  Shield,
  Clock
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { PaymentModal } from "@/components/PaymentModal"
import { createPayment } from "@/lib/payments"

// Precio puede venir de variable de entorno o usar default
// Para testing: 0.002 BNB (equivalente a ~$0.002 USD en testnet)
// El precio se muestra en USD pero el pago se hace en BNB cuando se selecciona BNB
const DEFAULT_PRICE = Number(import.meta.env.VITE_PREMIUM_PRICE_USD) || 0.002

const PREMIUM_PLAN = {
  name: "Premium Plan",
  price: DEFAULT_PRICE,
  duration: "Monthly",
  description: "Full access to all features and real-time signals"
}

const PREMIUM_PLAN_ES = {
  name: "Plan Premium",
  price: DEFAULT_PRICE,
  duration: "Mensual",
  description: "Acceso completo a todas las funciones y señales en tiempo real"
}

export function Payment() {
  const { language } = useLanguage()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { licenseType, reload } = useLicense(user?.uid)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const plan = language === "en" ? PREMIUM_PLAN : PREMIUM_PLAN_ES

  const handlePaymentComplete = async (paymentData: any) => {
    if (!user) {
      toast.error(language === "en" ? "You must be logged in" : "Debes estar conectado")
      return
    }

    setIsProcessing(true)

    try {
      // 1. Registrar pago en Firestore
      const paymentResult = await createPayment(
        user.uid,
        user.email || '',
        plan,
        paymentData
      )

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || 'Error al crear pago')
      }

      // 2. Enviar confirmación al backend (API endpoint)
      // Usar /api directamente para que funcione con vercel dev
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const apiUrl = apiBase.startsWith('http') ? `${apiBase}/payments/confirm` : `/api/payments/confirm`
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: user.displayName || user.email?.split('@')[0] || 'User',
            email: user.email,
            plan: plan,
            paymentData: paymentData,
            paymentId: paymentResult.paymentId
          })
        })

        if (!response.ok) {
          // Si el endpoint no está disponible (vercel dev no corriendo), solo mostrar warning
          console.warn('⚠️ API endpoint no disponible. El email se enviará cuando el servidor esté activo.')
          toast.warning(
            language === "en"
              ? "Payment recorded. Email will be sent when server is active."
              : "Pago registrado. El email se enviará cuando el servidor esté activo."
          )
        } else {
          // Email enviado exitosamente
          toast.success(
            language === "en"
              ? "Payment instructions sent to your email! Please check your inbox."
              : "¡Instrucciones de pago enviadas a tu email! Por favor revisa tu bandeja de entrada."
          )
        }
      } catch (fetchError) {
        // Error de conexión (vercel dev no corriendo)
        console.warn('⚠️ No se pudo conectar al API endpoint:', fetchError)
        toast.info(
          language === "en"
            ? "Payment recorded. To send email, run 'vercel dev' in terminal."
            : "Pago registrado. Para enviar email, ejecuta 'vercel dev' en la terminal."
        )
      }

      // 3. Cerrar modal PRIMERO
      setIsPaymentModalOpen(false)
      
      // 4. Mostrar mensaje de éxito
      toast.success(
        language === "en"
          ? "✅ Payment recorded successfully. Your transaction is queued for manual verification."
          : "✅ Pago registrado correctamente. Tu transacción quedó en cola para verificación manual."
      )

      // 5. Recargar licencia (por si acaso se activó automáticamente)
      reload()
      
      // 6. Redirigir a Settings después de 2 segundos
      setTimeout(() => {
        navigate("/settings")
      }, 2000)
    } catch (error) {
      console.error('Error processing payment:', error)
      toast.error(
        language === "en"
          ? "Error processing payment. Please contact support."
          : "Error al procesar el pago. Por favor contacta a soporte."
      )
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <InternalNavbar />

      <div className="container mx-auto px-4 py-8 max-w-4xl font-poppins">
        <div className="space-y-6">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/settings")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "en" ? "Back to Settings" : "Volver a Configuración"}
          </Button>

          {/* Page Header */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold flex items-center gap-3 text-foreground">
              <Crown className="w-9 h-9 text-primary" />
              {language === "en" ? "Upgrade to Premium" : "Actualizar a Premium"}
            </h1>
            <p className="text-base text-muted-foreground">
              {language === "en"
                ? "Get access to all premium features including private Telegram channel and unlimited signal history"
                : "Obtén acceso a todas las funciones premium incluyendo el canal privado de Telegram e historial ilimitado de señales"}
            </p>
          </div>

          {/* Premium Plan Card */}
          <Card className="border-primary/50 bg-gradient-to-br from-primary/10 to-secondary/10 shadow-xl shadow-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Crown className="w-6 h-6 text-primary" />
                    {language === "en" ? "Premium Plan" : "Plan Premium"}
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {language === "en"
                      ? "Full access to all features and real-time signals"
                      : "Acceso completo a todas las funciones y señales en tiempo real"}
                  </CardDescription>
                </div>
                <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0 text-base px-4 py-1">
                  {language === "en" ? "Premium" : "Premium"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Features List */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {language === "en" ? "What's included:" : "Qué está incluido:"}
                </h3>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-foreground">
                      {language === "en" ? "Private Telegram Channel" : "Canal Privado de Telegram"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === "en"
                        ? "Get all signals in real-time in your private Telegram channel"
                        : "Recibe todas las señales en tiempo real en tu canal privado de Telegram"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-foreground">
                      {language === "en" ? "Unlimited Signal History" : "Historial Ilimitado de Señales"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === "en"
                        ? "Access to complete signal history and performance metrics"
                        : "Acceso al historial completo de señales y métricas de rendimiento"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-foreground">
                      {language === "en" ? "All Signal Types" : "Todos los Tipos de Señales"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === "en"
                        ? "Receive all MT5 signals, not just high-quality ones"
                        : "Recibe todas las señales MT5, no solo las de alta calidad"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-foreground">
                      {language === "en" ? "Custom Notifications" : "Notificaciones Personalizadas"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === "en"
                        ? "Configure custom notification preferences for signals"
                        : "Configura preferencias personalizadas de notificaciones para señales"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="pt-6 border-t border-border/50">
                <div className="p-6 rounded-lg border border-primary/30 bg-primary/5">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {language === "en" ? "Crypto Payment System Ready" : "Sistema de Pago Cripto Listo"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === "en"
                          ? "Pay directly with your cryptocurrency wallet (MetaMask, Trust Wallet, etc.) or use manual payment with QR codes. Receive your license automatically via email after payment verification."
                          : "Paga directamente con tu billetera de criptomoneda (MetaMask, Trust Wallet, etc.) o usa pago manual con códigos QR. Recibe tu licencia automáticamente por email después de la verificación del pago."}
                      </p>
                    </div>
                  </div>

                  {/* Payment Features Preview */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="flex items-start gap-2 p-3 rounded-lg border border-border/50 bg-background/50">
                      <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-foreground">
                          {language === "en" ? "Multiple Wallets" : "Múltiples Billeteras"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === "en" ? "BTC, ETH, USDT" : "BTC, ETH, USDT"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 p-3 rounded-lg border border-border/50 bg-background/50">
                      <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-foreground">
                          {language === "en" ? "15 Min Window" : "15 Min de Ventana"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === "en" ? "Quick payment" : "Pago rápido"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 p-3 rounded-lg border border-border/50 bg-background/50">
                      <Lock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-foreground">
                          {language === "en" ? "Secure" : "Seguro"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === "en" ? "Verified payment" : "Pago verificado"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Button */}
                  <Button
                    onClick={() => setIsPaymentModalOpen(true)}
                    disabled={isProcessing || licenseType === 'premium' || licenseType === 'enterprise'}
                    size="lg"
                    className="w-full mt-6 gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {language === "en" ? "Processing..." : "Procesando..."}
                      </>
                    ) : licenseType === 'premium' || licenseType === 'enterprise' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        {language === "en" ? "You already have Premium" : "Ya tienes Premium"}
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4" />
                        {language === "en" ? "Proceed to Payment" : "Proceder al Pago"}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Security Notice */}
              <div className="flex items-start gap-3 p-4 rounded-lg border border-border/50 bg-muted/30">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    {language === "en" ? "Secure Payment" : "Pago Seguro"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === "en"
                      ? "All payments are processed securely. You will receive a license code via email after payment verification."
                      : "Todos los pagos se procesan de forma segura. Recibirás un código de licencia por email después de la verificación del pago."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        plan={plan}
        onPaymentComplete={handlePaymentComplete}
      />
    </div>
  )
}

